import React, { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { Route, Routes, useLocation } from 'react-router-dom'

import useAuthenticateContext from '../../hooks/useAuthenticate'
import { invoiceStateAction } from '../../store'
import { CustomToaster } from '../CustomToastMessage/CutomToaster'
import BackLink from '../misc/BackLink/BackLink'
import DelayedSpinner from '../misc/DelayedSpinner/DelayedSpinner'
import Navigation from '../misc/Navigation/Navigation'
import VerifyAccount from '../VerifyAccount' // Eagerly load first route
import styles from './Main.module.scss'
import { SloganProvider } from './SloganProvider'

// Lazy load other routes for better performance
const PaymentInfo = lazy(() => import('../PaymentInfo/PaymentInfo'))
const Review = lazy(() => import('../Review/Review'))
const ThankYou = lazy(() => import('../ThankYou/ThankYou'))

const steps = [
  { path: '/', label: 'Verify Account' },
  { path: '/payment', label: 'Payment Information' },
  { path: '/review', label: 'Submit Payment' },
  { path: '/thank-you', label: 'Thank You' },
]

export const MainContent: React.FC = () => {
  const location = useLocation()
  const isInvoice = useSelector((state: invoiceStateAction) => state.invoice.isInvoice)
  const { clearAuthenticatedData } = useAuthenticateContext()

  const currentIndex = steps.findIndex((step) => step.path === location.pathname)

  // BackLink logic: show for all except first and last step
  const showBackLink = currentIndex > 0 && currentIndex < steps.length - 1
  const backToStep = steps[currentIndex - 1]
  const backLabel = backToStep ? backToStep.label : ''
  // Only clear auth data when going all the way back to Verify Account. Navigating from
  // /review back to /payment must preserve the token so the Apollo client stays alive.
  const shouldClearOnBack = backToStep?.path === '/'

  const isThankYouPage = location.pathname === '/thank-you'

  return (
    <div className={styles.main}>
      {!isThankYouPage && (
        <>
          <SloganProvider isInvoice={isInvoice} />
          <div className={styles.navigation}>
            <Navigation activeIndex={currentIndex === -1 ? 0 : currentIndex} steps={steps.slice(0, 3)} />
            <BackLink
              to={backToStep ? backToStep.path : '/'}
              label={backLabel}
              show={showBackLink}
              onClick={shouldClearOnBack ? clearAuthenticatedData : undefined}
            />
          </div>
        </>
      )}
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-sm-10 col-lg-12">
          <Suspense fallback={<DelayedSpinner delay={200} />}>
            <Routes>
              <Route path="/" element={<VerifyAccount />} />
              <Route path="/payment" element={<PaymentInfo />} />
              <Route path="/review" element={<Review />} />
              <Route path="/thank-you" element={<ThankYou />} />
            </Routes>
          </Suspense>

          <CustomToaster />
        </div>
      </div>
    </div>
  )
}
